/**
 * useCaretInsert.ts — tracks the last-focused contenteditable element
 * and provides helpers to insert var-chips or plain text at the saved caret.
 *
 * Caller pattern (mousedown-preventDefault trick):
 *   onMouseDown={e => { e.preventDefault(); rememberFocus(); }}
 *   onClick={() => insertChip("stationName", "Tên station")}
 *
 * The mousedown preventDefault keeps the contenteditable focused, so
 * execCommand works against the live selection when onClick fires.
 */
import { useCallback, useRef } from "react";
import { buildChipHtml } from "./chipDom";
import { EmailBuilderActions } from "./useEmailBuilder";
import { writeBackFromEl } from "./writeBack";

export interface UseCaretInsertResult {
	/**
	 * Call on the Palette container's onMouseDown (with preventDefault) so the
	 * canvas editable never loses focus before the click handler fires.
	 * Also captures the active editable element + its blockId at that moment.
	 */
	rememberFocus: () => void;
	/**
	 * Insert an atomic var-chip at the saved caret.
	 * Uses execCommand('insertHTML') while the editable still has focus.
	 * After insertion, calls write-back so builder state is updated.
	 *
	 * @param name    Variable name, e.g. "stationName"
	 * @param label   Display label shown in the chip, e.g. "Tên station"
	 */
	insertChip: (name: string, label: string) => void;
	/**
	 * Insert a plain-text symbol at the saved caret.
	 * Uses execCommand('insertText').
	 * After insertion, calls write-back so builder state is updated.
	 */
	insertText: (ch: string) => void;
}

/**
 * Builds the full HTML string for a var-chip insertion, including a trailing
 * space so the caret lands after the chip.
 * Extracted as a pure function so tests can verify chip HTML without execCommand.
 */
export function buildInsertChipHtml(name: string, label: string): string {
	return buildChipHtml(name, label, false) + " ";
}

/** Find the nearest ancestor (or self) that is a contentEditable element. */
function findEditable(el: Element | null): HTMLElement | null {
	while (el) {
		if ((el as HTMLElement).isContentEditable) return el as HTMLElement;
		el = el.parentElement;
	}
	return null;
}

/** Extract blockId from the data-el attribute ("blockId:field[:index]"). */
function blockIdFromEl(el: HTMLElement): string | null {
	const dataEl = el.getAttribute("data-el");
	if (!dataEl) return null;
	return dataEl.split(":")[0] || null;
}

export function useCaretInsert(actions: EmailBuilderActions): UseCaretInsertResult {
	/** Last focused contenteditable that carries data-el. */
	const activeElRef = useRef<HTMLElement | null>(null);
	const activeBlockIdRef = useRef<string | null>(null);
	/**
	 * Caret position inside that editable at remember-time. Saved so click-insert
	 * works even when the palette item is NOT mousedown-preventDefaulted (var items
	 * must stay natively draggable, and preventDefault would block the drag).
	 */
	const savedRangeRef = useRef<Range | null>(null);

	const rememberFocus = useCallback(() => {
		const el = document.activeElement ? findEditable(document.activeElement) : null;
		if (el && el.getAttribute("data-el")) {
			activeElRef.current = el;
			activeBlockIdRef.current = blockIdFromEl(el);
			const sel = window.getSelection();
			savedRangeRef.current =
				sel && sel.rangeCount > 0 && el.contains(sel.anchorNode) ? sel.getRangeAt(0).cloneRange() : null;
		}
	}, []);

	/** Resolve the still-live editable (a re-render may have replaced the saved node). */
	const resolveLiveEl = (): HTMLElement | null => {
		let el = activeElRef.current;
		if (el && !document.contains(el)) {
			const dataEl = el.getAttribute("data-el");
			const live = dataEl ? document.querySelector(`[data-el="${dataEl}"]`) : null;
			if (live) {
				el = live as HTMLElement;
				activeElRef.current = el;
			}
		}
		return el;
	};

	/** Re-focus the saved editable and restore the caret before an execCommand insert. */
	const restoreCaret = (el: HTMLElement) => {
		el.focus();
		const sel = window.getSelection();
		if (sel && savedRangeRef.current) {
			try {
				sel.removeAllRanges();
				sel.addRange(savedRangeRef.current);
			} catch (_) {
				// stale range (DOM changed) — fall back to whatever focus() selected
			}
		}
	};

	const insertChip = useCallback(
		(name: string, label: string) => {
			const el = resolveLiveEl();
			const blockId = activeBlockIdRef.current;
			if (!el || !blockId) return;

			restoreCaret(el);
			const html = buildInsertChipHtml(name, label);
			try {
				document.execCommand("insertHTML", false, html);
			} catch (_) {
				// execCommand deprecated but still the most reliable cross-browser
				// insertion mechanism for contentEditable. No-op on failure.
			}

			writeBackFromEl(el, blockId, actions);
		},
		[actions]
	);

	const insertText = useCallback(
		(ch: string) => {
			const el = resolveLiveEl();
			const blockId = activeBlockIdRef.current;
			if (!el || !blockId) return;

			restoreCaret(el);
			try {
				document.execCommand("insertText", false, ch);
			} catch (_) {
				// no-op
			}

			writeBackFromEl(el, blockId, actions);
		},
		[actions]
	);

	return { rememberFocus, insertChip, insertText };
}
